<template>
    <div>
        <!-- Alerte erreur validation -->
        <div v-if="batchError" class="o-alert o-alert--error mb-3" role="alert">
            <div class="o-alert__icon">
                <svg class="svg-icon svg-circle-cross" aria-hidden="true" focusable="false">
                    <use xlink:href="/wp-content/themes/ans/design-system/svg-icons/icon-sprite.svg#circle-cross">
                    </use>
                </svg>
            </div>
            <h2 class="o-alert__title">Erreur de validation</h2>
            <p>{{ batchError }}</p>
            <button type="button" class="btn btn--close btn--icon-only" @click="batchError = null">
                <svg class="svg-icon svg-close" aria-hidden="true" focusable="false">
                    <use xlink:href="/wp-content/themes/ans/design-system/svg-icons/icon-sprite.svg#close">
                    </use>
                </svg>
                <span class="sr-only">Masquer ce message</span>
            </button>
        </div>

        <!-- Info avant sélection du fichier -->
        <div v-if="!batchError" class="o-alert o-alert--info mb-3" role="status">
            <div class="o-alert__icon">
                <svg class="svg-icon svg-circle-info" aria-hidden="true" focusable="false">
                    <use xlink:href="/wp-content/themes/ans/design-system/svg-icons/icon-sprite.svg#circle-info">
                    </use>
                </svg>
            </div>
            <h2 class="o-alert__title">{{ config.fileInput.messages.infoTitle }}</h2>
            <p>{{ config.fileInput.messages.infoBody }}</p>
        </div>

        <label for="input-batch-file" class="form-label fw-semibold">Fichier source (.xls, .xlsx)</label>
        <div class="d-flex align-items-center gap-2 mb-3 custom-file">
            <input id="input-batch-file" ref="fileInput" type="file" :accept="config.fileInput.accept"
                class="custom-file-input" :disabled="isLoading || isDisabled" @change="loadBatchFile"
                aria-label="Fichier Excel de traduction par lot" style="cursor: pointer;" />
            <label class="custom-file-label" for="input-batch-file"
                :data-browse="config.fileInput.messages.browseButtonLabel">
                {{ batchFileName || config.fileInput.messages.placeholder }}
            </label>
            <button v-if="batchFileName" type="button" class="btn btn--plain btn--primary text-nowrap"
                @click="clearBatch">
                <i class="bi bi-x-circle" aria-hidden="true"></i> Effacer
            </button>
        </div>
    </div>
</template>

<script>
import config from '../config/translator.config.js';

/**
 * Composant de saisie par fichier Excel.
 * Permet à l'utilisateur de sélectionner un fichier .xls/.xlsx et d'en lire le contenu.
 * Émet 'file-selected' avec les lignes parsées, 'file-invalid' en cas de validation
 * bloquante et 'clear' lors de la suppression.
 *
 * @prop  {Boolean} isLoading   - Désactive la saisie pendant une traduction.
 * @emits file-selected         - Tableau de lignes valides issues du fichier.
 * @emits file-invalid          - Fichier invalide (ex: dépassement de taille).
 * @emits clear                 - Lorsque le fichier est supprimé.
 */
export default {
    name: 'TranslatorFileInput',
    props: {
        /** Désactive l'input pendant le chargement. */
        isLoading: {
            type: Boolean,
            default: false
        },
        /** Désactive l'input quand l'utilisateur n'est pas connecté. */
        isDisabled: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            config,
            /** Nom du fichier sélectionné. */
            batchFileName: '',
            /** Message d'erreur de validation du fichier. */
            batchError: null
        }
    },
    methods: {
        loadBatchFile(event) {
            const file = event.target.files[0]
            if (!file) return

            const maxSizeBytes = config.fileInput.maxSizeBytes
            if (file.size > maxSizeBytes) {
                this.batchFileName = file.name
                this.batchError = config.fileInput.messages.errorFileTooLarge(maxSizeBytes / (1024 * 1024))
                this.$emit('file-invalid')
                return
            }

            const allowedExtensions = config.fileInput.allowedExtensions
            const fileExtension = file.name.slice(file.name.lastIndexOf('.')).toLowerCase()
            if (!allowedExtensions.includes(fileExtension)) {
                this.batchFileName = file.name
                this.batchError = config.fileInput.messages.errorInvalidExtension(fileExtension)
                this.$emit('file-invalid')
                return
            }

            this.batchFileName = file.name
            this.batchError = null
            this.$emit('file-selected', { file })
        },
        clearBatch() {
            this.batchFileName = ''
            this.batchError = null
            if (this.$refs.fileInput) {
                this.$refs.fileInput.value = ''
            }
            this.$emit('clear')
        }
    }
}
</script>

<style scoped>
.custom-file-label::after {
    content: attr(data-browse);
}
</style>
